import { Schedule, Session } from '../models/Schedule';
import { Speaker } from '../models/Speaker';
import { Location } from '../models/Location';

import { Storage } from '@capacitor/storage';
import {
  HAS_LOGGED_IN,
  HAS_SEEN_TUTORIAL,
  USERNAME,
  USERCED,
  USERID,
  USEROTHER,
  EVENTCODE,
  EVENT_NAME,
  EVENT_START_DATE,
  EVENT_END_DATE,
  EVENT_LOCATION
} from '../constants/storage';

// Importar Firebase para compatibilidad con código existente
import firebase from 'firebase/app';
import 'firebase/firestore';
import { firebaseConfig } from '../config/firebase.config';

// Inicializar Firebase si no está inicializado
if (!firebase.apps?.length) {
  firebase.initializeApp(firebaseConfig);
}


//const dataUrl = 'https://dev.esimposio.com/miesimposio/appendpoints/430C3626/?type=data&uid=123456';//https://esimposio.com/data.json
//const locationsUrl = 'https://dev.esimposio.com/miesimposio/appendpoints/430C3626/?type=locations&uid=123456';

let SCHEDULE_VERSION = "0";

/**
 * Verifica si hay una nueva versión disponible en el servidor
 * @param eventCode Código del evento
 * @returns Promise<{hasUpdate: boolean, serverVersion: string, currentVersion: string}>
 */
export const checkVersionUpdate = async (eventCode: string): Promise<{
  hasUpdate: boolean;
  serverVersion: string;
  currentVersion: string;
}> => {
  try {
    console.log("🔍 Verificando versión del servidor...");

    // Obtener versión actual del storage
    const currentVersionStorage = await Storage.get({ key: 'scheduleVersion' });
    const currentVersion = currentVersionStorage.value || SCHEDULE_VERSION || "0";

    // Consultar versión del servidor con el parámetro version=si
    const versionUrl = `https://esimposio.com/miesimposio/appendpoints/${eventCode}/?version=si`;
    console.log("📡 Consultando versión en:", versionUrl);

    const response = await fetch(versionUrl);

    if (!response.ok) {
      console.warn("⚠️ Error al consultar versión del servidor:", response.status);
      return {
        hasUpdate: false,
        serverVersion: currentVersion,
        currentVersion: currentVersion
      };
    }

    const versionData = await response.json();
    console.log("📦 Respuesta del servidor:", versionData);

    // Verificar que la respuesta tenga el formato esperado { "version": "1.8" }
    if (!versionData || !versionData.version) {
      console.warn("⚠️ Respuesta de versión inválida:", versionData);
      return {
        hasUpdate: false,
        serverVersion: currentVersion,
        currentVersion: currentVersion
      };
    }

    const serverVersion = versionData.version;

    // Comparar versiones (convertir a números para comparación)
    const currentVersionNum = parseFloat(currentVersion) || 0;
    const serverVersionNum = parseFloat(serverVersion) || 0;

    const hasUpdate = serverVersionNum > currentVersionNum;

    console.log(`📊 Comparación de versiones:
      - Actual: ${currentVersion} (${currentVersionNum})
      - Servidor: ${serverVersion} (${serverVersionNum})
      - Requiere actualización: ${hasUpdate}`);

    return {
      hasUpdate,
      serverVersion,
      currentVersion
    };

  } catch (error) {
    console.error("❌ Error al verificar versión:", error);
    return {
      hasUpdate: false,
      serverVersion: SCHEDULE_VERSION || "0",
      currentVersion: SCHEDULE_VERSION || "0"
    };
  }
};

/**
 * Función para verificar y actualizar datos automáticamente
 * Se puede llamar periódicamente o cuando la app se enfoque
 * @param eventCode Código del evento
 * @param forceUpdate Si es true, fuerza la actualización sin preguntar al usuario
 * @returns Promise<boolean> true si se actualizó, false si no
 */
export const checkAndUpdateData = async (eventCode: string, forceUpdate: boolean = false): Promise<boolean> => {
  try {
    console.log("🔍 Verificando actualizaciones de datos...");

    const versionCheck = await checkVersionUpdate(eventCode);

    if (!versionCheck.hasUpdate) {
      console.log("✅ No hay actualizaciones disponibles");
      return false;
    }

    console.log(`🆕 Nueva versión disponible: ${versionCheck.currentVersion} → ${versionCheck.serverVersion}`);

    // Si no es forzado, preguntar al usuario
    if (!forceUpdate) {
      const userConfirmed = window.confirm(
        `Hay una nueva versión de la agenda disponible (v${versionCheck.serverVersion}). ¿Desea actualizar ahora?`
      );

      if (!userConfirmed) {
        console.log("👤 Usuario canceló la actualización");
        return false;
      }
    }

    console.log("🔄 Iniciando actualización de datos...");

    // Actualizar la versión en memoria y storage
    SCHEDULE_VERSION = versionCheck.serverVersion;
    await Storage.set({ key: 'scheduleVersion', value: versionCheck.serverVersion });

    // Recargar los datos (esto disparará getConfData con la nueva versión)
    await getConfData();

    console.log("✅ Datos actualizados exitosamente");
    return true;

  } catch (error) {
    console.error("❌ Error al verificar/actualizar datos:", error);
    return false;
  }
};

export const getConfData = async () => {
  try {
    console.log("🔄 getConfData: INICIANDO carga de datos...");
    console.log("version antes:"+SCHEDULE_VERSION);

    // Obtener el código de evento del almacenamiento
    const resp = await Promise.all([Storage.get({ key: EVENTCODE })]);
    const evtCode = resp[0].value;

    // ✅ CORREGIDO: No cargar datos si no hay código de evento válido
    if (!evtCode || evtCode === "nada") {
      console.log("No hay código de evento válido, saltando carga de datos");
      // Devolver datos vacíos como fallback
      const emptySchedule: Schedule = { date: new Date().toISOString(), groups: [] };
      return {
        schedule: emptySchedule,
        sessions: [],
        locations: [],
        speakers: [],
        allTracks: [],
        filteredTracks: []
      };
    }

    console.log("Código de evento:", evtCode);

    // 🆕 VERIFICAR VERSIÓN ANTES DE CARGAR DATOS
    const versionCheck = await checkVersionUpdate(evtCode);

    if (versionCheck.hasUpdate) {
      console.log(`🔄 Nueva versión detectada: ${versionCheck.currentVersion} → ${versionCheck.serverVersion}`);

      // Actualizar la versión en memoria
      SCHEDULE_VERSION = versionCheck.serverVersion;

      // Guardar la nueva versión en storage
      await Storage.set({ key: 'scheduleVersion', value: versionCheck.serverVersion });

      // Opcional: Mostrar notificación al usuario
      console.log("📱 Actualizando datos con nueva versión...");
    } else {
      console.log(`✅ Versión actual (${versionCheck.currentVersion}) está actualizada`);

      // Cargar versión desde storage si no está en memoria
      if (SCHEDULE_VERSION === "0") {
        SCHEDULE_VERSION = versionCheck.currentVersion;
      }
    }

    // ✅ NUEVO: Intentar cargar datos desde DataLoadingService primero
    try {
      const DataLoadingService = (await import('../services/DataLoadingService')).default;
      const dataLoadingService = DataLoadingService.getInstance();

      // Verificar si ya hay datos cargados para este evento
      const loadedData = await dataLoadingService.loadEventData(evtCode, false);

      if (loadedData.success && loadedData.schedule) {
        console.log("✅ Datos cargados desde DataLoadingService para Redux");

        // Convertir los datos al formato esperado por Redux
        const data = {
          schedule: loadedData.schedule,
          sessions: loadedData.sessions || [],
          speakers: loadedData.speakers || [],
          locations: loadedData.locations || [],
          allTracks: loadedData.allTracks || [],
          filteredTracks: loadedData.allTracks || []
        };

        return data;
      }
    } catch (serviceError) {
      console.warn('Error al cargar desde DataLoadingService, fallback a API:', serviceError);
    }

    // ✅ FALLBACK: Si DataLoadingService falla, usar el método original
    console.log("Fallback: Cargando datos directamente desde API");

    // Realizar las solicitudes a la API
    const response = await Promise.all([
      fetch('https://esimposio.com/miesimposio/appendpoints/'+evtCode+'/?type=data&uid=123456'),
      fetch('https://esimposio.com/miesimposio/appendpoints/'+evtCode+'/?type=locations&uid=123456')
    ]);

    // Verificar si las respuestas son correctas
    if (!response[0].ok || !response[1].ok) {
      throw new Error('Error al obtener datos de la API');
    }

    // Procesar los datos de la respuesta
    let responseData;
    try {
      const rawData = await response[0].text();

      // Verificar si la respuesta está vacía
      if (!rawData || rawData.trim() === '') {
        console.warn('La respuesta de la API está vacía');
        throw new Error('Respuesta vacía');
      }

      // Intentar parsear el JSON
      try {
        responseData = JSON.parse(rawData);
      } catch (parseError) {
        console.error('Error al parsear la respuesta JSON:', parseError);
        console.warn('Respuesta inválida:', rawData);
        throw new Error('JSON inválido');
      }

      // Verificar si responseData es null o undefined
      if (!responseData) {
        console.warn('La respuesta de la API es null o undefined');
        throw new Error('Respuesta inválida');
      }
    } catch (error) {
      console.error('Error al procesar la respuesta:', error);
      // Crear un objeto responseData vacío como fallback
      responseData = { version: "1", schedule: [], speakers: [] };
    }

    // Verificar si es necesario actualizar la aplicación
    if(SCHEDULE_VERSION < responseData.version && SCHEDULE_VERSION != "0"){
      if (window.confirm('Se actualizará la aplicación')) {
        document.location.reload();
      }
    }

    SCHEDULE_VERSION = responseData.version || "1";
    console.log("version ahora:"+SCHEDULE_VERSION);

    // Verificar si responseData.schedule existe
    if (!responseData.schedule) {
      console.warn('responseData.schedule no existe:', responseData);
      responseData.schedule = [];
    }

    // Asegurarse de que responseData.schedule sea un array
    if (!Array.isArray(responseData.schedule)) {
      console.warn('responseData.schedule no es un array:', responseData.schedule);
      // Si no es un array, intentar convertirlo en uno
      try {
        if (typeof responseData.schedule === 'object' && responseData.schedule !== null) {
          // Si es un objeto, intentar convertirlo en un array con ese objeto
          responseData.schedule = [responseData.schedule];
        } else {
          // Si no es un objeto, crear un array vacío
          responseData.schedule = [];
        }
      } catch (error) {
        console.error('Error al convertir schedule en array:', error);
        responseData.schedule = [];
      }
    }

    // Procesar los datos normalmente
    let schedule: Schedule;

    // Verificar si responseData.schedule tiene al menos un elemento
    if (responseData.schedule.length > 0) {
      // Intentar usar el primer elemento
      try {
        schedule = responseData.schedule[0] as Schedule;

        // Verificar si schedule tiene la estructura esperada
        if (!schedule.date) {
          schedule.date = new Date().toISOString();
          console.warn('Schedule sin fecha, asignando fecha actual');
        }

        if (!schedule.groups) {
          schedule.groups = [];
          console.warn('Schedule sin grupos, asignando array vacío');
        } else if (!Array.isArray(schedule.groups)) {
          // Si groups no es un array, intentar convertirlo
          try {
            if (typeof schedule.groups === 'object' && schedule.groups !== null) {
              schedule.groups = [schedule.groups];
              console.warn('Schedule.groups no es un array, convertido a array');
            } else {
              schedule.groups = [];
              console.warn('Schedule.groups no es un objeto válido, asignando array vacío');
            }
          } catch (error) {
            console.error('Error al convertir schedule.groups:', error);
            schedule.groups = [];
          }
        }
      } catch (error) {
        console.error('Error al procesar el primer elemento de schedule:', error);
        schedule = { date: new Date().toISOString(), groups: [] };
      }
    } else {
      // Crear un schedule vacío como fallback
      console.warn('No se encontró un schedule válido en la respuesta:', responseData);
      schedule = { date: new Date().toISOString(), groups: [] };
    }

    const sessions = parseSessions(schedule);
    const speakers = Array.isArray(responseData.speakers) ? responseData.speakers as Speaker[] : [];
    console.log("Speakers cargados:", speakers.length);

    // Procesar las ubicaciones
    let locations: Location[] = [];
    try {
      const rawLocationsData = await response[1].text();

      // Verificar si la respuesta está vacía
      if (!rawLocationsData || rawLocationsData.trim() === '') {
        console.warn('La respuesta de ubicaciones está vacía');
      } else {
        // Intentar parsear el JSON
        try {
          const parsedData = JSON.parse(rawLocationsData);

          // Verificar si la respuesta es un objeto con una propiedad 'map'
          if (parsedData && typeof parsedData === 'object' && parsedData.map) {
            console.log('Encontrada propiedad map en la respuesta de ubicaciones');

            // Verificar si parsedData.map es un array
            if (Array.isArray(parsedData.map)) {
              locations = parsedData.map as Location[];
              console.log('Ubicaciones cargadas desde parsedData.map:', locations.length);
            } else {
              console.warn('parsedData.map no es un array:', parsedData.map);
            }
          }
          // Si no hay propiedad map o no es un array, verificar si la respuesta misma es un array
          else if (Array.isArray(parsedData)) {
            locations = parsedData as Location[];
            console.log('Ubicaciones cargadas directamente:', locations.length);
          } else {
            console.warn('La respuesta de ubicaciones no tiene la estructura esperada:', parsedData);

            // Intentar extraer cualquier propiedad que pueda ser un array de ubicaciones
            for (const key in parsedData) {
              if (Array.isArray(parsedData[key])) {
                console.log(`Intentando usar ${key} como array de ubicaciones`);
                locations = parsedData[key] as Location[];
                break;
              }
            }
          }
        } catch (parseError) {
          console.error('Error al parsear las ubicaciones JSON:', parseError);
          console.warn('Respuesta de ubicaciones inválida:', rawLocationsData);
        }
      }
    } catch (error) {
      console.warn('Error al procesar las ubicaciones:', error);
    }

    // Si no se pudieron cargar ubicaciones, crear algunas predeterminadas
    if (locations.length === 0) {
      console.warn('No se pudieron cargar ubicaciones, usando valores predeterminados');
      locations = [
        { id: 1, name: 'Sala Principal', lat: 0, lng: 0 },
        { id: 2, name: 'Sala de Conferencias', lat: 0, lng: 0 },
        { id: 3, name: 'Auditorio', lat: 0, lng: 0 }
      ];
    }

    // Calcular las pistas (tracks) disponibles
    const allTracks = sessions
      .reduce((all, session) => {
        // Verificar si session.tracks existe y es un array
        if (session.tracks && Array.isArray(session.tracks)) {
          return all.concat(session.tracks);
        }
        return all;
      }, [] as string[])
      .filter((trackName, index, array) => array.indexOf(trackName) === index)
      .sort();

    const data = {
      schedule,
      sessions,
      locations,
      speakers,
      allTracks,
      filteredTracks: [...allTracks]
    }

    return data;
  } catch (error) {
    console.error('Error al cargar los datos de la conferencia:', error);

    // Devolver datos vacíos como fallback
    const emptySchedule: Schedule = { date: new Date().toISOString(), groups: [] };

    return {
      schedule: emptySchedule,
      sessions: [],
      locations: [],
      speakers: [],
      allTracks: [],
      filteredTracks: []
    };
  }
}

export const getUserData = async () => {
  try {
    // Obtener datos del almacenamiento
    const response = await Promise.all([
      Storage.get({ key: HAS_LOGGED_IN }),
      Storage.get({ key: HAS_SEEN_TUTORIAL }),
      Storage.get({ key: SCHEDULE_VERSION }),
      Storage.get({ key: USERNAME }),
      Storage.get({ key: USERCED }),
      Storage.get({ key: USERID }),
      Storage.get({ key: USEROTHER }),
      Storage.get({ key: EVENTCODE }),
      Storage.get({ key: EVENT_NAME }),
      Storage.get({ key: EVENT_START_DATE }),
      Storage.get({ key: EVENT_END_DATE }),
      Storage.get({ key: EVENT_LOCATION })
    ]);

    // Procesar los valores obtenidos
    const isLoggedin = response[0].value === 'true';
    const hasSeenTutorial = response[1].value === 'true';
    const username = response[3].value || undefined;
    const userced = response[4].value || undefined;
    const userid = response[5].value || undefined;
    const userother = response[6].value || undefined;
    const eventcode = response[7].value || undefined;
    const eventName = response[8].value || undefined;
    const eventStartDate = response[9].value || undefined;
    const eventEndDate = response[10].value || undefined;
    const eventLocation = response[11].value || undefined;

    // Devolver los datos del usuario
    return {
      isLoggedin,
      hasSeenTutorial,
      username,
      userced,
      userid,
      userother,
      eventcode,
      eventName,
      eventStartDate,
      eventEndDate,
      eventLocation
    };
  } catch (error) {
    console.error('Error al obtener datos del usuario:', error);

    // Devolver valores predeterminados en caso de error
    return {
      isLoggedin: false,
      hasSeenTutorial: false,
      username: undefined,
      userced: undefined,
      userid: undefined,
      userother: undefined,
      eventcode: undefined,
      eventName: undefined,
      eventStartDate: undefined,
      eventEndDate: undefined,
      eventLocation: undefined
    };
  }
}

export const setIsLoggedInData = async (isLoggedIn: boolean) => {
  await Storage.set({ key: HAS_LOGGED_IN, value: JSON.stringify(isLoggedIn) });
}

export const setHasSeenTutorialData = async (hasSeenTutorial: boolean) => {
  await Storage.set({ key: HAS_SEEN_TUTORIAL, value: JSON.stringify(hasSeenTutorial) });
}

export const setUsernameData = async (username?: string) => {
  if (!username) {
    await Storage.remove({ key: USERNAME });
  } else {
    await Storage.set({ key: USERNAME, value: username });
  }
}

export const setUserCedData = async (userced?: string) => {
  if (!userced) {
    await Storage.remove({ key: "USERCED" });
  } else {
    await Storage.set({ key: USERCED, value: userced });
  }
}

export const setUserIDData = async (userid?: string) => {
  if (!userid) {
    await Storage.remove({ key: USERID });
  } else {
    await Storage.set({ key: USERID, value: userid });
  }
}

export const setUserOtherData = async (userother?: string) => {
  if (!userother) {
    await Storage.remove({ key: USEROTHER });
  } else {
    await Storage.set({ key: USEROTHER, value: userother });
  }
}

export const setEventCodeData = async (eventcode?: string) => {
  if (!eventcode) {
    await Storage.remove({ key: EVENTCODE });
  } else {
    await Storage.set({ key: EVENTCODE, value: eventcode });
  }
}

export const setEventNameData = async (eventName?: string) => {
  if (!eventName) {
    await Storage.remove({ key: EVENT_NAME });
  } else {
    await Storage.set({ key: EVENT_NAME, value: eventName });
  }
}

export const setEventStartDateData = async (eventStartDate?: string) => {
  if (!eventStartDate) {
    await Storage.remove({ key: EVENT_START_DATE });
  } else {
    await Storage.set({ key: EVENT_START_DATE, value: eventStartDate });
  }
}

export const setEventEndDateData = async (eventEndDate?: string) => {
  if (!eventEndDate) {
    await Storage.remove({ key: EVENT_END_DATE });
  } else {
    await Storage.set({ key: EVENT_END_DATE, value: eventEndDate });
  }
}

export const setEventLocationData = async (eventLocation?: string) => {
  if (!eventLocation) {
    await Storage.remove({ key: EVENT_LOCATION });
  } else {
    await Storage.set({ key: EVENT_LOCATION, value: eventLocation });
  }
}

function parseSessions(schedule: Schedule) {
  const sessions: Session[] = [];

  // Verificar si schedule y schedule.groups existen antes de intentar iterar
  if (schedule && schedule.groups && Array.isArray(schedule.groups)) {
    schedule.groups.forEach((g, groupIndex) => {
      try {
        // Verificar si g.sessions existe y es un array
        if (g && g.sessions && Array.isArray(g.sessions)) {
          g.sessions.forEach((s, sessionIndex) => {
            try {
              // Verificar que la sesión tenga las propiedades mínimas necesarias
              if (!s) {
                console.warn(`Sesión ${sessionIndex} en grupo ${groupIndex} es null o undefined`);
                return; // Continuar con la siguiente sesión
              }

              // Asegurarse de que la sesión tenga un ID
              if (!s.id) {
                // Generar un ID si no existe
                s.id = `generated-${groupIndex}-${sessionIndex}-${Date.now()}`;
                console.warn(`Generando ID para sesión sin ID: ${s.id}`);
              }

              // Asegurarse de que la sesión tenga un nombre
              if (!s.name) {
                s.name = `Sesión ${sessionIndex + 1}`;
                console.warn(`Generando nombre para sesión sin nombre: ${s.id}`);
              }

              // Asegurarse de que la sesión tenga tracks
              if (!s.tracks || !Array.isArray(s.tracks) || s.tracks.length === 0) {
                s.tracks = ['General'];
                console.warn(`Asignando track predeterminado para sesión sin tracks: ${s.id}`);
              }

              // Asegurarse de que la sesión tenga speakerNames
              if (!s.speakerNames || !Array.isArray(s.speakerNames)) {
                s.speakerNames = ['Sin conferencista asignado'];
                console.warn(`Asignando conferencista predeterminado para sesión sin conferencistas: ${s.id}`);
              }

              // Asegurarse de que la sesión tenga una fecha válida
              if (!s.date) {
                s.date = new Date().toISOString().split('T')[0]; // Solo la fecha YYYY-MM-DD
                console.warn(`Asignando fecha predeterminada para sesión sin fecha: ${s.id}`);
              }

              // Asegurarse de que la sesión tenga horas de inicio y fin
              if (!s.timeStart) {
                s.timeStart = new Date().toTimeString().split(' ')[0]; // Solo la hora HH:MM:SS
                console.warn(`Asignando hora de inicio predeterminada para sesión: ${s.id}`);
              }

              if (!s.timeEnd) {
                // Hora de fin = hora de inicio + 1 hora
                const endTime = new Date();
                endTime.setHours(endTime.getHours() + 1);
                s.timeEnd = endTime.toTimeString().split(' ')[0]; // Solo la hora HH:MM:SS
                console.warn(`Asignando hora de fin predeterminada para sesión: ${s.id}`);
              }

              // Asegurarse de que la sesión tenga una ubicación
              if (!s.location) {
                s.location = 'Ubicación por definir';
                console.warn(`Asignando ubicación predeterminada para sesión: ${s.id}`);
              }

              // Asegurarse de que la sesión tenga una descripción
              if (!s.description) {
                s.description = 'Sin descripción disponible';
                console.warn(`Asignando descripción predeterminada para sesión: ${s.id}`);
              }

              // Añadir la sesión al array de sesiones
              sessions.push(s);
            } catch (sessionError) {
              console.error(`Error al procesar sesión ${sessionIndex} en grupo ${groupIndex}:`, sessionError);
            }
          });
        } else {
          console.warn(`El grupo ${groupIndex} no tiene sesiones válidas:`, g);
        }
      } catch (groupError) {
        console.error(`Error al procesar grupo ${groupIndex}:`, groupError);
      }
    });
  } else {
    console.warn('La estructura de schedule no es la esperada:', schedule);
  }

  return sessions;
}
